import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';
import { useDangerSignStore } from '../stores/dangerSign.store';
import { dangerSignConditionNames } from '../utils/dangerSignConditions';
import { scoreDangerSigns } from '../utils/scoreDangerSigns';
import type { DangerSignFinding, DangerSignObservation } from '../types';

export type DangerSignCheckOutcome = 'continue' | 'stop' | 'failed' | 'stale';

const OUTCOME_BY_ACTION: Record<string, DangerSignCheckOutcome> = {
  acknowledge: 'continue',
  requestStop: 'stop',
  beginCheck: 'stale',
  supersede: 'stale',
  reset: 'stale',
  resume: 'stale',
};

export function useDangerSignCheck() {
  const store = useDangerSignStore();

  const awaitChoice = (): Promise<DangerSignCheckOutcome> => new Promise(resolve => {
    const unsubscribe = store.$onAction(({ name, after }) => {
      const outcome = OUTCOME_BY_ACTION[name];
      if (!outcome) return;
      after(() => {
        unsubscribe();
        resolve(outcome);
      });
    }, true);
  });

  const runCheck = async (observations?: DangerSignObservation[]): Promise<DangerSignCheckOutcome> => {
    const names = dangerSignConditionNames();
    if (!names.length) return 'continue';

    const version = store.beginCheck();
    let positives: DangerSignFinding[];
    try {
      positives = await scoreDangerSigns(names, observations);
    } catch (error) {
      if (version !== store.checkVersion) return 'stale';
      logger.error(
        LogCategory.ENCOUNTER,
        'danger_sign.check.failed',
        {
          type: error instanceof Error ? error.name : typeof error,
          reason: error instanceof Error ? error.message : String(error),
        },
        'Danger sign check failed',
      ).then();
      return 'failed';
    }

    if (!store.applyCheck(version, positives)) return 'stale';
    return store.hasPendingAlert ? awaitChoice() : 'continue';
  };

  const cancel = (): void => {
    store.supersede();
  };

  return { runCheck, cancel };
}
